/*!
  Pioneer WFRIA WaitMessage v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["WaitMessage"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("WaitMessage") < 0) {
    window.__PIONEER_GLOBAL__.components.push("WaitMessage");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["container", "disableAutoFocus", "customStrings", "elementToFocusOnShow", "percentage", "render", "renderFocusableContent", "type", "timer", "onClose", "elementToFocusOnClose", "afterFocusOnClose", "zIndex"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/* eslint no-bitwise:0, max-depth:0, no-lonely-if:0 */
import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef } from "react";
import { DELAY_TIME, KEYBOARD_KEYS, ZINDEX } from "../common/constants";
import { useCountdown, useUniqueId } from "../common/hooks";
import { deprecated, invalidIf, requiredIf } from "../common/proptypes";
import { getFocusableElements } from "../common/utilities";
import LiveRegion from "../private/LiveRegion/LiveRegion";
import Popup from "../private/Popup/Popup";
import ProgressIndicator from "../private/ProgressIndicator/ProgressIndicator";
import extractTheme from "./extractTheme";
import STRINGS from "./strings.js";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const MAXIMUM_PERCENTAGE = 100;
const PERCENTAGE_ANNOUNCEMENTS = [26, 51, 76, 87, 97, MAXIMUM_PERCENTAGE];
const ONE_MINUTE = 60;
const TIMER_ANNOUNCEMENTS = [30 * ONE_MINUTE, 20 * ONE_MINUTE, 10 * ONE_MINUTE, 7 * ONE_MINUTE, 4 * ONE_MINUTE, ONE_MINUTE, 45, 30, 15, 7, 4, 1, 0];
const TIMER_ANNOUNCEMENTS_MAP = TIMER_ANNOUNCEMENTS.reduce((map, step) => {
  map[step] = true;
  return map;
}, {});
const POLITENESS_WHEN_ENABLED = "assertive";
/**
 * The WaitMessage component renders an obtrusive or unobtrusive message that can
 * include a timer and focusable elements.
 */
// eslint-disable-next-line complexity

function WaitMessage(_ref) {
  let {
    container,
    disableAutoFocus,
    // eslint-disable-line no-unused-vars
    customStrings,
    elementToFocusOnShow,
    percentage,
    render,
    renderFocusableContent,
    type,
    timer,
    onClose,
    elementToFocusOnClose,
    afterFocusOnClose,
    zIndex
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    cssRoot,
    cssBody,
    cssOverlay,
    cssProgressIndicator
  } = extractTheme({
    theme,
    type
  });

  const {
    remaining,
    timerComplete
  } = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const isObtrusive = useCallback(() => {
    return container() === document.body;
  }, [container]);
  const triggerRef = useRef(document.activeElement);
  const popupRef = useRef();
  const contentContainerRef = useRef();
  const contentContainerIsFocused = useRef(false);
  const dynamicAnnouncementsEnabledRef = useRef(false);
  const mayAnnounceRef = useRef(isObtrusive());
  const messagesContainerId = useUniqueId("pnr-waitmessage__messages-");
  /**
   * We use a new Array per instance since we mutate it directly
   * and don't want to impact the original.
   */

  const remainingPercentageAnnouncementsRef = useRef([...PERCENTAGE_ANNOUNCEMENTS]);
  const [result, {
    startCountdown,
    stopCountdown
  }] = useCountdown(timer || {});
  useEffect(() => {
    const contentContainerElement = contentContainerRef.current;
    /**
     * This method is responsible for calling the callback handler `afterFocusOnClose`
     * prop passed by the consumers only if it exists.
     */

    const callAfterFocusOnCloseProp = () => {
      if (afterFocusOnClose) {
        setTimeout(() => {
          afterFocusOnClose();
        }, DELAY_TIME);
      }
    };
    /**
     * Focuses the element that needs to receive focus after
     * certain delay. This is needed for screen readers to announce
     * the element that would receive focus once the WaitMessage unmounts.
     */


    const focusElementAfterDelay = elementToFocus => {
      setTimeout(() => {
        elementToFocus && elementToFocus.focus();
        callAfterFocusOnCloseProp();
      }, DELAY_TIME);
    };

    if (timer) {
      startCountdown();
    } // Set initial focus.


    if (isObtrusive()) {
      const elementToFocus = (elementToFocusOnShow || contentContainerRef).current;

      if (elementToFocus) {
        elementToFocus.focus();
      }
    }

    return () => {
      stopCountdown();
      /**
       * On WaitMessage close, focusable element will be determined by `elementToFocusOnClose` prop.
       */

      if (elementToFocusOnClose) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const elementToFocus = elementToFocusOnClose.current;
        focusElementAfterDelay(elementToFocus);
      } else if (isObtrusive()) {
        focusElementAfterDelay(triggerRef.current);
      } else {
        if (contentContainerIsFocused.current === true) {
          /**
           * Focus is currently on, or within, the `WaitMessage`,
           * so attempt to focus on the first focusable
           * element in `props.container()`. This needs to be done after
           * Popup has completed removing aria-hidden attributes, so
           * setTimeout is used.
           */
          setTimeout(() => {
            const waitMessageContainer = container();
            const firstFocusableElement = waitMessageContainer && getFocusableElements(waitMessageContainer)[0];

            if (firstFocusableElement && firstFocusableElement !== contentContainerElement && // `.focus()` on svg element in IE throws an error, so add defensive check.
            firstFocusableElement.focus) {
              firstFocusableElement.focus();
            } else {
              triggerRef.current && // eslint-disable-next-line react-hooks/exhaustive-deps
              triggerRef.current.focus();
            }
          }, 0);
        }

        callAfterFocusOnCloseProp();
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * Disables AT announcements even if the message content changes.
   */

  const disableDynamicAnnouncements = () => {
    if (dynamicAnnouncementsEnabledRef.current) {
      const {
        current: contentContainerElement
      } = contentContainerRef;
      contentContainerElement.setAttribute("aria-live", "off");
      dynamicAnnouncementsEnabledRef.current = false;
    }
  };
  /**
   * Enables AT announcements when message content changes.
   */


  const enableDynamicAnnouncements = () => {
    const {
      current: contentContainerElement
    } = contentContainerRef;

    if (contentContainerElement) {
      contentContainerElement.setAttribute("aria-live", POLITENESS_WHEN_ENABLED);
      dynamicAnnouncementsEnabledRef.current = true;
    }
  };

  const handleBlur = () => {
    // Unobtrusive WaitMessage SHOULD NOT announce unless it has focus.
    mayAnnounceRef.current = isObtrusive();
    contentContainerIsFocused.current = false;
  };

  const handleFocus = () => {
    mayAnnounceRef.current = true;
    contentContainerIsFocused.current = true;
  };
  /**
   * Handler called on keydown of the WaitMessage container.
   *
   * @param {EventFacade} event The keydown event facade.
   */


  const handleKeyDown = event => {
    const key = event.key;
    /**
     * As per the a11y requirements WaitMessage should be hidden on escape key press only
     * if any additional focusable content is rendered within the WaitMessage,
     * so checking if `renderFocusableContent` prop is passed on in order to call the
     * `onClose` handler.
     */

    if (renderFocusableContent && key === KEYBOARD_KEYS.ESCAPE) {
      onClose();
    }
  };

  const renderContent = () => {
    const hasPercentage = percentage !== null;

    if (hasPercentage) {
      let shouldAnnounce = false;

      while (percentage >= remainingPercentageAnnouncementsRef.current[0]) {
        shouldAnnounce = true;
        remainingPercentageAnnouncementsRef.current.shift();
      }

      if (mayAnnounceRef.current && (shouldAnnounce || percentage >= MAXIMUM_PERCENTAGE)) {
        enableDynamicAnnouncements();
        return _jsx(LiveRegion, {
          politeness: POLITENESS_WHEN_ENABLED,
          visible: true,
          children: render()
        });
      }
    }

    disableDynamicAnnouncements();
    return _jsxs(React.Fragment, {
      children: [render(), mayAnnounceRef.current && !hasPercentage && _jsx(LiveRegion, {
        politeness: POLITENESS_WHEN_ENABLED,
        announcementDelay: 1000,
        clearAnnouncementDelay: 1000,
        children: render()
      })]
    });
  };

  const renderTimerContent = () => {
    const {
      internalCount,
      formattedCount
    } = result; // If the user supplied `props.render`, use it. Otherwise, default to just displaying the formattedTime.

    return render ? render({
      formattedTime: formattedCount,
      time: internalCount,
      timeInMilliseconds: internalCount * 1000
    }) : _jsx("span", {
      className: "pnr-waitmessage__defaultTimer",
      children: internalCount === 0 ? timerComplete : `${formattedCount} ${remaining}`
    });
  };

  const renderTimer = () => {
    const {
      internalCount
    } = result;

    if (mayAnnounceRef.current && TIMER_ANNOUNCEMENTS_MAP[internalCount]) {
      enableDynamicAnnouncements();
      return _jsx(LiveRegion, {
        politeness: POLITENESS_WHEN_ENABLED,
        visible: true,
        children: renderTimerContent()
      });
    }

    disableDynamicAnnouncements();
    return renderTimerContent();
  };

  const isProgressType = type === "progress";
  const notStaticWM = percentage || timer;
  const isObtrusiveWaitMessage = isObtrusive();
  let progressIndicatorProgress = null;

  if (isProgressType) {
    if (percentage !== null) {
      progressIndicatorProgress = percentage;
    } else if (timer !== null) {
      const {
        internalCount
      } = result;
      progressIndicatorProgress = (timer.timeLimit - internalCount) / timer.timeLimit * 100;
    }
  }

  return _jsx(Popup, _objectSpread(_objectSpread({
    modal: isObtrusiveWaitMessage,
    lockScroll: isObtrusiveWaitMessage,
    nested: true,
    open: true,
    onClose: onClose,
    rootNode: !isObtrusiveWaitMessage ? container() : null,
    elementToFocusOnOpen: elementToFocusOnShow ? () => elementToFocusOnShow : null,
    noFocusElementOnOpen: !isObtrusiveWaitMessage ? true : null,
    ref: popupRef,
    type: isObtrusiveWaitMessage ? "obtrusiveWaitMessage" : "unObtrusiveWaitMessage",
    disableFocusTrap: true,
    contentStyle: cssRoot,
    overlayStyle: cssOverlay,
    repositionOnResize: !isObtrusiveWaitMessage,
    zIndex: zIndex,
    ariaModal: renderFocusableContent && isObtrusiveWaitMessage
  }, otherProps), {}, {
    children: _jsxs("div", {
      ref: contentContainerRef,
      css: cssBody
      /**
       * FF/NVDA will only announce the characters that change if we don't set `aria-atomic` to "true".
       * For example, it will only announce "5" when changing from "46 seconds" to "45 seconds".
       */
      ,
      "aria-atomic": notStaticWM ? true : null,
      "aria-labelledby": renderFocusableContent ? messagesContainerId : null,
      "aria-live": notStaticWM ? "off" : null,
      className: "pnr-waitmessage__content-container",
      onBlur: handleBlur,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      role: renderFocusableContent ? "alertdialog" : null,
      tabIndex: "0",
      "aria-modal": renderFocusableContent && isObtrusiveWaitMessage ? "true" : null,
      children: [_jsx(ProgressIndicator, {
        decorative: true,
        progress: progressIndicatorProgress,
        display: "inline-block",
        spacing: {
          b: 2
        },
        css: cssProgressIndicator,
        "data-testid": "waitmessage__progressIndicator"
      }), (render || timer) && _jsx("div", {
        "data-testid": "waitmessage__messages",
        id: messagesContainerId,
        children: timer ? renderTimer() : renderContent()
      }), renderFocusableContent && _jsx("div", {
        "data-testid": "waitmessage__focusableContent",
        children: renderFocusableContent()
      })]
    })
  }));
}

WaitMessage.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Callback handler that fires after the WaitMessage finishes focusing on the element
   * before it unmounts.
   * Use this prop in cases where any other component (Eg: MessageBar) needs to be shown
   * immediately once the WaitMessage unmounts.
   */
  afterFocusOnClose: PropTypes.func,

  /**
   * A function which returns a container node to which the WaitMessage should be rendered.
   * Defaults to a function that returns `document.body` if not provided.
   */
  container: PropTypes.func,

  /**
   * Override for strings used within the component.
   *  - `timerComplete`: Announcement for the completion of the timer.
   *     Default to "Timer complete".
   */
  customStrings: PropTypes.shape({
    remaining: PropTypes.string,
    timerComplete: PropTypes.string
  }),

  /**
   * Previous incarnations of `WaitMessage` focused on Unobtrusive WaitMessages automatically, so this prop
   * allowed users to override this behavior. Now that we DO NOT focus on Unobtrusive WaitMessages, this
   * prop is no longer valid.
   *
   * @ignore
   */
  disableAutoFocus: deprecated(PropTypes.bool, `It no longer applies with the updated accessibility rules, and is now no-op`),

  /**
   * This prop identifies the element that should receive
   * focus after WaitMessage is closed.
   * It expects a [ref](https://reactjs.org/docs/refs-and-the-dom.html).
   */
  elementToFocusOnClose: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.instanceOf(React.Component)])
  }),

  /**
   * A ref to an element to focus when showing the WaitMessage. Typically this element is rendered by the
   * `renderFocusableContent` function. This may be an Element, or a React.Component that exposes
   * a `focus` method.
   *
   * Required if you supply the `renderFocusableContent` prop for an obtrusive WaitMessage.
   */
  elementToFocusOnShow: requiredIf(PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.instanceOf(React.Component)])
  }), (props, propName) => !props[propName] && props.renderFocusableContent !== null && props.container() === document.body, `if "props.renderFocusableContent" is supplied for obtrusive WaitMessage`),

  /**
   * Callback handler that fires when you should close the `WaitMessage`.
   *
   * Note: The `WaitMessage` will never close itself. As per the a11y requirements, the `WaitMessage`
   * should be hidden on escape key press, as the WaitMessage display state is completely controlled,
   * it's required that the application teams handle this logic via `onClose` callback handler.
   *
   * Required if you supply the `renderFocusableContent` prop.
   */
  onClose: requiredIf(PropTypes.func, (props, propName) => !props[propName] && props.renderFocusableContent !== null, `if "props.renderFocusableContent" is supplied`),

  /**
   * Percentage of the progress bar width.
   *
   * Applicable for `WaitMessage` of `type="progress"`.
   */
  percentage: invalidIf(PropTypes.number, (props, propName) => props[propName] && props.timer, `percentage cannot be used with timer.`),

  /**
   * A render prop responsible for rendering the `WaitMessage`'s content.
   *
   * NOTE: To render focusable content, such as a `Button`, use the `renderFocusableContent` prop.
   */
  render: PropTypes.func,

  /**
   * A render prop responsible for rendering one or more focusable elements, such as a `Button`, after the
   * `WaitMessage` content (i.e. after `props.render`).
   *
   * Required if you supply the `elementToFocusOnShow` prop.
   */
  renderFocusableContent: requiredIf(PropTypes.func, (props, propName) => !props[propName] && props.elementToFocusOnShow !== null, `if "props.elementToFocusOnShow" is supplied`),

  /**
   * Timer configuration.
   */
  timer: invalidIf(PropTypes.shape({
    format: PropTypes.oneOf(["m:s", "s"]),
    interval: PropTypes.number,
    onComplete: PropTypes.func,
    threshold: PropTypes.number,
    timeLimit: PropTypes.number
  }), (props, propName) => props[propName] && props.percentage, `timer cannot be used with percentage.`),

  /**
   * The type of progress indicator to display.
   *
   * Defaults to an indefinite spinner if not provided.
   *
   * NOTE: `type="progress"` only applies when `props.timer` is also supplied.
   */
  type: PropTypes.oneOf(["bar", "progress", "spinner"]),

  /**
   * Allowing the user to provide a different z-index if needed.
   */
  zIndex: PropTypes.number
} : {};
WaitMessage.defaultProps = {
  container: () => document.body,
  customStrings: null,
  disableAutoFocus: null,
  elementToFocusOnShow: null,
  percentage: null,
  render: null,
  renderFocusableContent: null,
  type: "spinner",
  timer: null,
  onClose: null,
  elementToFocusOnClose: null,
  afterFocusOnClose: null,
  zIndex: ZINDEX.WAIT_MESSAGE
};
export default WaitMessage;